import {
  Lit3formBlock,
  html,
  unsafeHTML,
  classMap,
} from '@Root/lib/lit-3form-block';
import { styles } from './css';
import { analyticsCtaDataObject } from '../../../helpers/adobe-analytics';

export class Santiago extends Lit3formBlock {
  static get characterName() {
    return 'santiago';
  }

  static get characterProperties() {
    return {
      cta: { type: String },
    };
  }

  static get characterStyles() {
    return [...styles];
  }

  render() {
    return html`
      <div class="block-wrapper">
        <div class="${classMap(this.layoutClasses)}">
          ${this.headerHTML} ${this.descriptionHTML}
          <div class="${classMap(this.driversClasses)}">
            ${this.cardGroups.map(cardGroup => {
              return cardGroup.driver_cards.map(card =>
                this.driverCardHTML(card)
              );
            })}
          </div>
          ${this.horizontalRule}
        </div>
      </div>
    `;
  }

  get layoutClasses() {
    return {
      'block-grid': true,
      'santiago-layout': true,
      'no-image-border': this.hideImageBorder,
    };
  }

  get driversClasses() {
    return {
      drivers: true,
      [`drivers-count-${this.driverCount}`]: true,
      'two-per-row': this.cardsPerRow === 'two',
      'three-per-row': this.cardsPerRow === 'three',
      'four-per-row': this.cardsPerRow === 'four',
    };
  }

  // The field is required and existing blocks were backfilled, so the count
  // fallback only covers content saved before either landed.
  get cardsPerRow() {
    if (this.content.cards_per_row) return this.content.cards_per_row;

    return this.driverCount === 4 ? 'four' : 'three';
  }

  // Avo stores content booleans as '0'/'1'.
  get hideImageBorder() {
    return this.content.hide_image_border == '1';
  }

  get driverCount() {
    return this.cardGroups.reduce(
      (total, cardGroup) => total + (cardGroup.driver_cards?.length || 0),
      0
    );
  }

  driverCardHTML(driver) {
    return html`
      <div class="driver-wrapper">
        ${this.driverInternalHtml(driver)}
      </div>
    `;
  }

  driverInternalHtml(driver) {
    const callToActionList = driver.call_to_actions || [];
    const placement = this.cardPlacement(driver);

    return html`
      <driver-card
        .card=${driver}
        placement=${placement}
        download-placement=${this.headerText}
        image-width=${this.imageWidth}
      ></driver-card>
      ${this.textSectionsHTML(driver)}
      ${callToActionList.map(cta => {
        // isDownload arrives on the CTA when it resolves to a document, so a
        // plain link keeps its arrow and reports as navigation.
        cta.fullWidth = true;
        return html`
          <cta-button
            cta-data=${JSON.stringify(cta)}
            .ctaAnalyticsData=${analyticsCtaDataObject(cta, placement)}
          ></cta-button>
        `;
      })}
      ${this.cardCtaTextHTML(driver, placement)}
    `;
  }

  // Without a block header the placement would be the block's codename, so
  // a headerless block names each card's clicks after the card's title.
  cardPlacement(driver) {
    return this.headerText || driver.title || this.analyticsBlockLabel;
  }

  // The driver card's own "CTA Text" + URL pair, rendered as a text link the
  // way Clark and Elle do. Separate from any Call To Action list items, which
  // render as buttons above.
  cardCtaTextHTML(driver, placement) {
    if (!driver.text) return html``;

    return html`
      <cta-text
        class="card-cta-text"
        cta-data=${JSON.stringify({
          text: driver.text,
          url: driver.url,
        })}
        .ctaAnalyticsData=${analyticsCtaDataObject(driver, placement)}
      ></cta-text>
    `;
  }

  // Borderless cards let the image fill the card, so it needs to come back
  // large enough for the widest card at 2x.
  get imageWidth() {
    if (!this.hideImageBorder) return 300;

    return this.cardsPerRow === 'two' ? 1400 : 900;
  }

  textSectionsHTML(driver) {
    return driver.text_sections?.length > 0
      ? html`
          <div class="text-sections-wrapper">
            ${driver.text_sections.map(section => {
              return html`
                <span class="text-section">
                  <p>
                    <b>${unsafeHTML(this.withLineBreaks(section.heading))}</b>
                    ${section.paragraphs.map(paragraph => {
                      return unsafeHTML(this.withLineBreaks(paragraph.text));
                    })}
                  </p>
                </span>
              `;
            })}
          </div>
        `
      : html``;
  }

  get cardGroups() {
    return !this.content.card_groups || this.content.card_groups.length === 0
      ? []
      : this.content.card_groups;
  }

  get headerHTML() {
    return this.headerText
      ? html`
          <div class="header details-row">
            <h2>${unsafeHTML(this.headerText)}</h2>
          </div>
        `
      : ``;
  }

  get descriptionHTML() {
    return this.descriptionText
      ? html`
          <p class="description details-row">
            ${unsafeHTML(this.descriptionText)}
          </p>
        `
      : ``;
  }
}
Santiago.defineElement();
